// Imports
import React, { useContext, useEffect, useState } from 'react';
import { useAppDispatch } from '../../../../shareables/foundation/front-end/redux/hooks';
import { useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import Box from '../../../../shareables/foundation/admin-ui/components/content/box';
import Heading from '../../../../shareables/foundation/admin-ui/components/content/heading';
import Table from '../../../../shareables/foundation/admin-ui/components/content/table';
import apiActions from '../../../../shareables/integrations/api/redux/actions';
import theme from '../../../../shareables/theme';
import { GetTransactionResponse } from '../../../../shareables/types/api/main/reseller/billing/transaction/get';
import formatCurrency from '../../../../shareables/utils/project-specific/formatting/format-currency';
import company from '../../../../shareables/company';
import TransactionDetailRow from './row';
import ResellerContext from '../../../resources/reseller-provider/context';
import Logo from '../../../../shareables/front-end/svg-logo';


// Styled components
const TopPart = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: 1fr 1fr;
	align-items: center;
	row-gap: 1.25rem;
	margin-bottom: 1.5rem;
	
	@media (min-width: ${theme.breakpoints.mobile + 1}px) {
		> div:nth-child(even) {
			text-align: right;
		}
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		grid-template-columns: 1fr;
		grid-auto-flow: row;
		row-gap: 0.75rem;
	}
`;

const LogoContainer = styled.div`
	width: 18.75rem;
	
	> svg {
		width: 100%;
		height: auto;
	}
	
	padding-right: 2em;
`;


// Define params
type Params = {
	transactionID: string;
};


// Define the accepted props
interface Props {
	storybookTransaction?: GetTransactionResponse;
}


// Function component
const Receipt: React.FC<Props> = ({ storybookTransaction }) => {
	// Use state
	const [transaction, setTransaction] = useState<GetTransactionResponse | null>(storybookTransaction ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const { transactionID } = useParams<Params>();
	
	
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Load transaction on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetTransactionResponse>({
				action: 'GET',
				uri: `/reseller/${reseller?.id ?? ''}/billing/transaction/${transactionID ?? ''}`,
				completion: (json) => {
					setTransaction(json.response);
				},
			})
		);
	}, [dispatch, transactionID, reseller?.id]);
	
	
	// If loading, then display nothing
	if (!transaction) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Heading size={1}>View receipt</Heading>
			
			<Box>
				<TopPart>
					<LogoContainer>
						<Logo />
					</LogoContainer>
					
					<div>
						<div>
							{new Date(transaction.received).toLocaleDateString(undefined, {
								year: 'numeric',
								month: 'long',
								day: 'numeric',
								timeZone: 'America/Chicago',
							})}
						</div>
						<div>Reference #: {transaction.id}</div>
					</div>
					<div>
						<div>HOA Express</div>
						<div>www.{company.productionDomain}</div>
						<div>support@{company.productionDomain}</div>
					</div>
					
					<div>{transaction.reseller}</div>
				</TopPart>
				
				{Boolean(transaction.chargedAmount) && (
					<Table>
						<thead>
							<tr>
								<th>Payment method</th>
							</tr>
						</thead>
						
						<tbody>
							<tr>
								<td>{transaction.paymentMethodDescriptor}</td>
							</tr>
						</tbody>
					</Table>
				)}
				
				<Table>
					<thead>
						<tr>
							<th>Item</th>
							<th style={{ textAlign: 'right' }}>Amount</th>
						</tr>
					</thead>
					
					<tbody>
						{[
							...(transaction.communities ?? []).map((community, i) => ({
								value: community.amount,
								content: (line: { allIntegers: boolean; value: number; amount: string }) => (
									<tr key={`${i}_community`}>
										<td>
											<div>{community.name}</div>
											<div>
												<small>
													<em>{community.descriptor}</em>
												</small>
											</div>
										</td>
										<td style={{ textAlign: 'right' }}>{line.amount}</td>
									</tr>
								),
							})),
							...(!transaction.communities && Boolean(transaction.minimumSpendCredits)
								? [
										{
											value: transaction.minimumSpendCredits * -1,
											content: (line: { allIntegers: boolean; value: number; amount: string }) => (
												<tr key='minimum_spend_makeup'>
													<td>{transaction.minimumSpendMonth} minimum spend makeup</td>
													<td style={{ textAlign: 'right' }}>{line.amount}</td>
												</tr>
											),
										},
									]
								: []),
							...(transaction.communities
								? [
										{
											value: transaction.communities.reduce((acc, { amount }) => acc + amount, 0),
											content: (line: { allIntegers: boolean; value: number; amount: string }) => (
												<TransactionDetailRow
													key='subtotal'
													label='Subtotal'
													amount={line.value}
													hideCentsIfInteger={line.allIntegers}
													isStrong={true}
												/>
											),
										},
										{
											value: transaction.resellerDiscount * -1,
											content: (line: { allIntegers: boolean; value: number; amount: string }) => (
												<TransactionDetailRow
													key='reseller_discount'
													label='Reseller discount'
													amount={line.value}
													hideCentsIfInteger={line.allIntegers}
													isStrong={false}
												/>
											),
										},
										...(transaction.minimumSpendCredits
											? [
													{
														value: transaction.minimumSpendCredits * -1,
														content: (line: { allIntegers: boolean; value: number; amount: string }) => (
															<TransactionDetailRow
																key={`${transaction.minimumSpendMonth}_minimum_spend_${
																	transaction.minimumSpendCredits < 0 ? 'makeup' : 'credit'
																}`}
																label={`${transaction.minimumSpendMonth} minimum spend ${
																	transaction.minimumSpendCredits < 0 ? 'makeup' : 'credit'
																}`}
																amount={line.value}
																hideCentsIfInteger={line.allIntegers}
																isStrong={false}
															/>
														),
													},
												]
											: []),
									]
								: []),
							...(transaction.minimumChargeDiscount
								? [
										{
											value: (transaction.minimumChargeDiscount ?? 0) * -1,
											content: (line: { allIntegers: boolean; value: number; amount: string }) => (
												<TransactionDetailRow
													key='minimum_charge_discount'
													label='Minimum charge discount'
													amount={line.value}
													hideCentsIfInteger={line.allIntegers}
													isStrong={false}
												/>
											),
										},
									]
								: []),
							...(transaction.salesTax
								? [
										{
											value: transaction.salesTax,
											content: (line: { allIntegers: boolean; value: number; amount: string }) => (
												<TransactionDetailRow
													key='sales_tax'
													label='Sales tax'
													amount={line.value}
													hideCentsIfInteger={line.allIntegers}
													isStrong={!transaction.communities}
												/>
											),
										},
									]
								: []),
							{
								value: transaction.chargedAmount,
								content: (line: { allIntegers: boolean; value: number; amount: string }) => (
									<TransactionDetailRow
										key='total'
										label='Total'
										amount={line.value}
										isStrong={true}
										hideCentsIfInteger={line.allIntegers}
									/>
								),
							},
						].map((row, i, all) => {
							const allIntegers = all.every((row) => Number.isInteger(row.value));
							return row.content({
								allIntegers: allIntegers,
								value: row.value,
								amount: formatCurrency(row.value, {
									hideCentsIfInteger: allIntegers,
									currency: 'USD',
									hideCurrencyCode: (reseller?.country?.code ?? 'USA') === 'USA',
								}),
							});
						})}
					</tbody>
				</Table>
			</Box>
		</React.Fragment>
	);
};

export default Receipt;
